import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { fetchJobOverview, fetchJobOverviewActivity } from "../../Redux/ActionCreators/jobsAction";
import "./jobInsightsV2.scss";

const ACTIVITY_PAGE_SIZE = 5;

const PERIOD_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "15", label: "Last 15 days" },
  { value: "30", label: "Last 30 days" },
  { value: "custom", label: "Custom" },
];

// Used when a hiring status comes back with color: null
const NO_STATUS_COLOR = "#9ca3af";
const FALLBACK_COLORS = ["#2563eb", "#059669", "#d97706", "#7c3aed", "#0891b2", "#65a30d", "#db2777", "#475569"];

const statusKey = (s) => s?.id || "none";

// 10% tint of a #rrggbb color for badge backgrounds
const tint = (hex) => (/^#[0-9a-f]{6}$/i.test(hex || "") ? `${hex}1a` : "#f3f4f6");

const recruiterKey = (r) => (r.is_unassigned ? "unassigned" : String(r.user_id));
const recruiterName = (r) => (r.is_unassigned ? "Unassigned" : r.name || r.email || "Recruiter");

// react-select styled to match the page controls (jobsV2 palette)
const recruiterSelectStyles = {
  container: (base) => ({ ...base, minWidth: 260, maxWidth: 520, flex: "0 1 auto" }),
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    borderRadius: 8,
    borderColor: state.isFocused ? "#e91e8c" : "#e4e9f0",
    boxShadow: "none",
    fontSize: 13,
    "&:hover": { borderColor: state.isFocused ? "#e91e8c" : "#c4cad3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  placeholder: (base) => ({ ...base, color: "#8a9199" }),
  dropdownIndicator: (base) => ({ ...base, padding: 6 }),
  clearIndicator: (base) => ({ ...base, padding: 6 }),
  indicatorSeparator: () => ({ display: "none" }),
  multiValue: (base) => ({ ...base, background: "#fdf0f7", borderRadius: 99 }),
  multiValueLabel: (base) => ({ ...base, color: "#e91e8c", fontSize: 12, fontWeight: 600, padding: "2px 2px 2px 8px" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#e91e8c",
    borderRadius: 99,
    "&:hover": { background: "#e91e8c", color: "#fff" },
  }),
  menu: (base) => ({ ...base, zIndex: 20, fontSize: 13 }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected ? "#fdf0f7" : state.isFocused ? "#f8f9fb" : "#fff",
    color: state.isSelected ? "#e91e8c" : "#1a1e23",
    fontWeight: state.isSelected ? 600 : 400,
  }),
};

const fmtPct =(value) => (value === null || value === undefined ? "—" : `${Number(value).toFixed(1)}%`);

const initials = (name = "") =>
  name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const formatAgo = (seconds) => {
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
};

const RefreshIcon = ({ spinning }) => (
  <svg className={spinning ? "rp-spin" : ""} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

// The page's selected period ({ from, to } as YYYY-MM-DD), carried into every drill link.
const DrillRangeContext = createContext(null);

// Candidates page, Activity-wise tab, pre-filtered. The Activity-wise view maps these params
// onto its filters: stage (comma-separated ids), rowStatus (done | in-progress | pending |
// expired), hiring (status id | "hired" | "none"), recruiter (user id | "unassigned"),
// recruiterName, from / to.
const drillHref = (jobId, params, range) => {
  const query = new URLSearchParams({ view: "activity" });
  Object.entries({ ...params, ...(range?.from ? { from: range.from, to: range.to } : {}) })
    .forEach(([k, v]) => { if (v) query.set(k, v); });
  return `/jobs/${jobId}/candidates?${query}`;
};

// Number/label that links through to the Activity-wise list pre-filtered by `params`
const Drill = ({ params = {}, children }) => {
  const { jobId } = useParams();
  const range = useContext(DrillRangeContext);
  return (
    <Link to={drillHref(jobId, params, range)} className="rp-drill" title="View matching records in Activity-wise">
      {children}
    </Link>
  );
};

// A stage can be addressed by its job-stage id or its master stage id; send both and let
// the Activity-wise view keep whichever its Stage filter knows.
const stageParam = (s) => ({ stage: [...new Set([s.job_hiring_stage_id, s.hiring_stage_id].filter(Boolean))].join(",") });

const JobInsightsV2 = () => {
  const { jobId } = useParams();
  const dispatch = useDispatch();

  const [period, setPeriod] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [recruiterView, setRecruiterView] = useState("cards");
  const [selectedRecruiters, setSelectedRecruiters] = useState([]);

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");

  const [activity, setActivity] = useState({ items: [], recordedCount: 0 });
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [activityUpdatedAt, setActivityUpdatedAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  // Ignore responses from requests that were superseded by a newer one
  const overviewReq = useRef(0);
  const activityReq = useRef(0);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // null = custom range not complete yet, so keep showing the last result
  const queryRange = useMemo(() => {
    if (!period) return { from: "", to: "" };
    if (!startDate || !endDate) return null;
    return { from: moment(startDate).format("YYYY-MM-DD"), to: moment(endDate).format("YYYY-MM-DD") };
  }, [period, startDate, endDate]);

  const loadOverview = useCallback(async () => {
    if (!jobId || !queryRange) return;
    const reqId = ++overviewReq.current;
    setOverviewLoading(true);
    setOverviewError("");
    const res = await dispatch(fetchJobOverview(jobId, queryRange.from, queryRange.to));
    if (reqId !== overviewReq.current) return;
    if (res?.success) {
      setOverview(res.data);
    } else {
      setOverviewError(res?.message || "Failed to load job overview");
    }
    setOverviewLoading(false);
  }, [dispatch, jobId, queryRange]);

  const loadActivity = useCallback(async () => {
    if (!jobId || !queryRange) return;
    const reqId = ++activityReq.current;
    setActivityLoading(true);
    setActivityError("");
    const res = await dispatch(fetchJobOverviewActivity(jobId, ACTIVITY_PAGE_SIZE, queryRange.from, queryRange.to));
    if (reqId !== activityReq.current) return;
    if (res?.success) {
      const data = res.data || {};
      setActivity({ items: data.items || [], recordedCount: data.recorded_count ?? 0 });
      setActivityUpdatedAt(Date.now());
    } else {
      setActivityError(res?.message || "Failed to load job activity");
    }
    setActivityLoading(false);
  }, [dispatch, jobId, queryRange]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const handlePeriodChange = (value) => {
    setPeriod(value);
    if (value === "custom") {
      setDateRange([null, null]);
    } else {
      setDateRange([moment().subtract(Number(value) - 1, "days").startOf("day").toDate(), new Date()]);
    }
  };

  const resetPeriod = () => {
    setPeriod("");
    setDateRange([null, null]);
  };

  const hasPeriod = Boolean(period && (startDate || endDate));

  const summary = overview?.summary || {};
  const totalCandidates = summary.total_candidates ?? overview?.hiring_status?.total ?? 0;
  const byStatus = useMemo(() => overview?.hiring_status?.by_status || [], [overview]);
  const stages = overview?.stage_progression || [];
  const recruiterOutcomes = overview?.recruiter_outcomes || {};
  const recruiters = useMemo(() => overview?.recruiter_outcomes?.recruiters || [], [overview]);
  const isPeriodMode = overview?.mode && overview.mode !== "current";

  // One color per hiring status, shared by the summary band and recruiter cards
  const statusColors = useMemo(() => {
    const map = {};
    let fallbackIdx = 0;
    byStatus.forEach((s) => {
      map[statusKey(s)] = s.color || (s.id ? FALLBACK_COLORS[fallbackIdx++ % FALLBACK_COLORS.length] : NO_STATUS_COLOR);
    });
    return map;
  }, [byStatus]);
  const colorOf = (s) => statusColors[statusKey(s)] || s.color || NO_STATUS_COLOR;

  const recruiterOptions = useMemo(
    () => recruiters.map((r) => ({ value: recruiterKey(r), label: recruiterName(r) })),
    [recruiters]
  );

  // Nothing selected = show every recruiter
  const filteredRecruiters = useMemo(() => {
    if (selectedRecruiters.length === 0) return recruiters;
    const keys = new Set(selectedRecruiters.map((o) => o.value));
    return recruiters.filter((r) => keys.has(recruiterKey(r)));
  }, [selectedRecruiters, recruiters]);

  const rangeLabel = `${startDate ? moment(startDate).format("DD MMM") : "…"} – ${endDate ? moment(endDate).format("DD MMM") : "…"}`;
  const presetLabel = period !== "custom" ? PERIOD_OPTIONS.find((o) => o.value === period)?.label : "";
  const periodLabel = hasPeriod ? (presetLabel ? `${presetLabel} · ${rangeLabel}` : rangeLabel) : "";

  const recruiterParam = (r) => ({ recruiter: recruiterKey(r), recruiterName: r.is_unassigned ? "" : recruiterName(r) });
  // Only carry the period into drill links once it's a complete range.
  const drillRange = hasPeriod && queryRange?.from ? queryRange : null;

  const renderStatusBadge = (s) => {
    const color = colorOf(s);
    return (
      <span className="rp-badge" style={{ color, background: tint(color) }}>
        {s.label}
      </span>
    );
  };

  return (
    <div className="rp-wrap">
      <div className="rp-toolbar" role="region" aria-label="Activity date filter">
        <div className="rp-scope" aria-live="polite">
          <span className={`rp-mode ${hasPeriod ? "on" : ""}`}>{hasPeriod ? "Recorded activity" : "Current totals"}</span>
          <span className="txt">
            {hasPeriod
              ? `Recorded activity · ${periodLabel}`
              : "Current snapshot · select a period for recorded activity"}
          </span>
        </div>
        <div className="rp-dates">
          <label className="rp-period">
            <span>Period</span>
            <select aria-label="Activity period" value={period} onChange={(e) => handlePeriodChange(e.target.value)}>
              <option value="" disabled>
                Select period
              </option>
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {period === "custom" && (
            <div className="rp-period rp-custom">
              <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => {
                setDateRange(update);
              }}
              isClearable={true}
              maxDate={new Date()}
              dateFormat="dd MMM"
              className="form-control date-range-picker"
              placeholderText="Select date range"
              />
            </div>
          )}
          <button className="rp-btn" disabled={!period} onClick={resetPeriod}>
            <RefreshIcon />
            Reset
          </button>
        </div>
      </div>

      {!overview && overviewLoading && <p className="rp-state">Loading job insights…</p>}

      {overviewError && (
        <p className="rp-state error">
          {overviewError}
          <button className="rp-link-btn" onClick={loadOverview}>
            Try again
          </button>
        </p>
      )}

      <DrillRangeContext.Provider value={drillRange}>
      <div className={`rp-sheet ${overviewLoading && overview ? "is-loading" : ""}`}>
        {overview && (
          <>
            {/* ── Summary ─────────────────────────────────────────────────── */}
            <section className="rp-summary" aria-labelledby="rp-sum-h">
              <div className="rp-headline">
                <p className="rp-eyebrow" id="rp-sum-h">
                  Candidates in this job · {isPeriodMode ? "added in period" : "current"}
                </p>
                <b>
                  <Drill>{totalCandidates}</Drill>
                </b>
                <span>
                  Across {summary.pipeline_stage_count ?? stages.length} pipeline stage
                  {(summary.pipeline_stage_count ?? stages.length) === 1 ? "" : "s"}
                </span>
              </div>
              <div className="rp-statusband">
                <div className="rp-statusband-h">
                  <h2>Hiring status</h2>
                  <span>Share of {overview.hiring_status?.total ?? totalCandidates} candidates</span>
                </div>
                <div className="rp-stack" role="img" aria-label="Hiring status distribution">
                  {byStatus.filter((s) => s.count > 0).map((s) => (
                    <i key={statusKey(s)} style={{ flexGrow: s.count, background: colorOf(s) }} />
                  ))}
                </div>
                <hr className="rp-hr" />
                {byStatus.length === 0 ? (
                  <p className="rp-act-empty">No hiring statuses configured for this job.</p>
                ) : (
                  <ul className="rp-legend">
                    {byStatus.map((s) => {
                      const color = colorOf(s);
                      return (
                        <li key={statusKey(s)}>
                          <span className="sw" style={{ background: color }} />
                          <span className="lb" style={{ color, background: tint(color) }}>
                            {s.label}
                          </span>
                          <b>
                            <Drill params={{ hiring: statusKey(s) }}>{s.count}</Drill>
                          </b>
                          <em>{fmtPct(s.pct)}</em>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>

            {/* ── 01 · Pipeline ───────────────────────────────────────────── */}
            <section className="rp-section" aria-labelledby="rp-pipe-h">
              <header className="rp-sec-h">
                <div>
                  <p className="rp-eyebrow">01 · Pipeline</p>
                  <h2 id="rp-pipe-h">Stage progression</h2>
                </div>
                <p>Current state of every applicable candidate. Rate = completed / applicable.</p>
              </header>
              {stages.length === 0 ? (
                <p className="rp-act-empty">No hiring stages configured for this job.</p>
              ) : (
                <ol className="rp-flow">
                  {stages.map((stage, i) => {
                    const idx = String(stage.order ?? i + 1).padStart(2, "0");
                    const key = stage.job_hiring_stage_id || stage.hiring_stage_id || i;

                    if (!stage.has_data) {
                      return (
                        <li key={key} className="na">
                          <div className="top">
                            <span className="idx">{idx}</span>
                            <div className="lbl-wrap">
                              <span className="lbl" title={stage.name}>{stage.name}</span>
                              <span className="tag none">Not yet recorded</span>
                            </div>
                          </div>
                          <div className="prog">
                            <div className="rate">
                              <b>—</b>
                              <span>No matching stage data</span>
                            </div>
                            <span className="rp-meter empty" aria-hidden="true" />
                          </div>
                          <dl className="off" aria-label="No breakdown available">
                            <div><dt>In progress</dt><dd>—</dd></div>
                            <div className="pend"><dt>Pending</dt><dd>—</dd></div>
                            <div className="exp"><dt>Expired</dt><dd>—</dd></div>
                          </dl>
                        </li>
                      );
                    }

                    const rate = fmtPct(stage.completion_pct);
                    const sp = stageParam(stage);
                    const ratio = stage.applicable ? stage.completed / stage.applicable : 0;

                    return (
                      <li key={key}>
                        <div className="top">
                          <span className="idx">{idx}</span>
                          <div className="lbl-wrap">
                            <span className="lbl" title={stage.name}>{stage.name}</span>
                            <span className="tag">Recorded</span>
                          </div>
                        </div>
                        <div className="prog">
                          <div className="rate">
                            <b><Drill params={{ ...sp, rowStatus: "done" }}>{rate}</Drill></b>
                            <span>
                              <Drill params={{ ...sp, rowStatus: "done" }}>{stage.completed}</Drill> /{" "}
                              <Drill params={sp}>{stage.applicable}</Drill> completed
                            </span>
                          </div>
                          <span className="rp-meter" role="img" aria-label={`${rate} completed`}>
                            <i style={{ transform: `scaleX(${ratio})` }} />
                          </span>
                        </div>
                        <dl>
                          <div><dt>In progress</dt><dd><Drill params={{ ...sp, rowStatus: "in-progress" }}>{stage.in_progress}</Drill></dd></div>
                          <div className="pend"><dt>Pending</dt><dd><Drill params={{ ...sp, rowStatus: "pending" }}>{stage.pending}</Drill></dd></div>
                          <div className="exp"><dt>Expired</dt><dd><Drill params={{ ...sp, rowStatus: "expired" }}>{stage.expired}</Drill></dd></div>
                        </dl>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>

            {/* ── 02 · Ownership ──────────────────────────────────────────── */}
            <section className="rp-section" aria-labelledby="rp-rec-h">
              <header className="rp-sec-h">
                <div>
                  <p className="rp-eyebrow">02 · Ownership</p>
                  <h2 id="rp-rec-h">Recruiter outcomes</h2>
                </div>
                <p>Candidates grouped by current recruiter. Stage results show completed / recruiter's candidates.</p>
              </header>

              <div className="rp-rec-toolbar">
                <div className="sa-seg" role="group" aria-label="Recruiter view">
                  <button className={recruiterView === "cards" ? "on" : ""} aria-pressed={recruiterView === "cards"} onClick={() => setRecruiterView("cards")}>
                    Cards
                  </button>
                  <button className={recruiterView === "list" ? "on" : ""} aria-pressed={recruiterView === "list"} onClick={() => setRecruiterView("list")}>
                    List
                  </button>
                </div>
                <Select
                  isMulti
                  className="rp-rec-select"
                  classNamePrefix="rp-rs"
                  aria-label="Filter recruiters"
                  placeholder="All recruiters"
                  options={recruiterOptions}
                  value={selectedRecruiters}
                  onChange={(vals) => setSelectedRecruiters(vals || [])}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  noOptionsMessage={() => "No recruiters"}
                  styles={recruiterSelectStyles}
                />
                <span>
                  {selectedRecruiters.length > 0
                    ? `${filteredRecruiters.length} of ${recruiters.length} recruiters`
                    : `${recruiters.length} recruiter${recruiters.length === 1 ? "" : "s"}`}
                </span>
              </div>

              {recruiterOutcomes.has_assignments === false && recruiters.length > 0 && (
                <p className="rp-note">
                  No recruiter is assigned yet, so all candidates appear under <b>Unassigned</b>.
                </p>
              )}

              {filteredRecruiters.length === 0 ? (
                <p className="rp-act-empty">
                  {recruiters.length === 0 ? "No candidates in this job yet." : "The selected recruiters have no candidates for this period."}
                </p>
              ) : recruiterView === "cards" ? (
                <ul className="rp-rcards">
                  {filteredRecruiters.map((r) => {
                    const rp = recruiterParam(r);
                    const name = recruiterName(r);
                    const tiles = (r.hiring_status || []).filter((s) => s.id && s.count > 0);
                    return (
                      <li key={recruiterKey(r)} className={r.is_unassigned ? "unassigned" : ""}>
                        <div className="rc-h">
                          <span className="rp-av" aria-hidden="true">{r.is_unassigned ? "–" : initials(name)}</span>
                          <div>
                            <strong title={r.email || ""}><Drill params={rp}>{name}</Drill></strong>
                            <span className="rp-cnt">
                              <b><Drill params={rp}>{r.candidate_count ?? 0}</Drill></b> candidate{r.candidate_count === 1 ? "" : "s"}
                            </span>
                          </div>
                          <div className="rc-rate">
                            <b><Drill params={{ ...rp, hiring: "hired" }}>{fmtPct(r.hire_rate_pct)}</Drill></b>
                            <span>Hire rate ({r.hired_count ?? 0}/{r.candidate_count ?? 0})</span>
                          </div>
                        </div>
                        <div className="rc-body">
                          {tiles.length === 0 ? (
                            <p className="rc-out-empty">No hiring status set for these candidates yet.</p>
                          ) : (
                            <dl
                              className="rc-out"
                              aria-label="Outcomes"
                              style={{ gridTemplateColumns: `repeat(${Math.min(tiles.length, 3)}, minmax(0, 1fr))` }}
                            >
                              {tiles.map((s) => (
                                <div key={statusKey(s)}>
                                  <dt>{renderStatusBadge(s)}</dt>
                                  <dd style={{ color: colorOf(s) }}>
                                    <Drill params={{ ...rp, hiring: statusKey(s) }}>{s.count}</Drill>
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          )}
                          <div className="rc-stages">
                            <p className="rp-eyebrow">
                              Stages completed · <Drill params={{ ...rp, rowStatus: "done" }}>{r.stages_completed_total ?? 0} total</Drill>
                            </p>
                            <dl aria-label="Stages completed">
                              {(r.stages || []).map((s) => (
                                <div key={s.job_hiring_stage_id || s.name}>
                                  <dt>{s.name}</dt>
                                  <dd>
                                    <Drill params={{ ...rp, ...stageParam(s), rowStatus: "done" }}>{s.completed}</Drill>
                                    <span className="sub">/{s.out_of}</span>
                                  </dd>
                                  <dd className="rp-mini" aria-hidden="true">
                                    <i style={{ transform: `scaleX(${s.out_of ? s.completed / s.out_of : 0})` }} />
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="rp-rtable-wrap">
                  <table className="rp-rtable">
                    <thead>
                      <tr>
                        <th>Recruiter</th>
                        <th>Candidates</th>
                        <th>Completed stages</th>
                        <th>Hiring status</th>
                        <th>Hired</th>
                        <th>Hire rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecruiters.map((r) => {
                        const rp = recruiterParam(r);
                        const name = recruiterName(r);
                        const tiles = (r.hiring_status || []).filter((s) => s.id && s.count > 0);
                        return (
                          <tr key={recruiterKey(r)}>
                            <td className="rec" title={r.email || ""}>
                              <Drill params={rp}>{name}</Drill>
                            </td>
                            <td><Drill params={rp}>{r.candidate_count ?? 0}</Drill></td>
                            <td className="stg">
                              <Drill params={{ ...rp, rowStatus: "done" }}>{r.stages_completed_total ?? 0}</Drill>
                              <details className="rp-bystage">
                                <summary>By stage</summary>
                                <dl>
                                  {(r.stages || []).map((s) => (
                                    <div key={s.job_hiring_stage_id || s.name}>
                                      <dt>{s.name}</dt>
                                      <dd>
                                        <Drill params={{ ...rp, ...stageParam(s), rowStatus: "done" }}>{s.completed}</Drill>
                                      </dd>
                                    </div>
                                  ))}
                                </dl>
                              </details>
                            </td>
                            <td>
                              {tiles.length === 0 ? (
                                <span className="rp-muted">—</span>
                              ) : (
                                <div className="rp-chips">
                                  {tiles.map((s) => (
                                    <Link
                                      key={statusKey(s)}
                                      to={drillHref(jobId, { ...rp, hiring: statusKey(s) }, drillRange)}
                                      className="rp-badge"
                                      style={{ color: colorOf(s), background: tint(colorOf(s)) }}
                                    >
                                      {s.label} · {s.count}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td><Drill params={{ ...rp, hiring: "hired" }}>{r.hired_count ?? 0}</Drill></td>
                            <td><Drill params={{ ...rp, hiring: "hired" }}>{fmtPct(r.hire_rate_pct)}</Drill></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="rp-caveat">
                Grouping uses today's owner, not who owned the candidate when an event happened. Differences can reflect
                candidate mix and sample size.
              </p>
            </section>
          </>
        )}

        {/* ── 03 · Log ────────────────────────────────────────────────── */}
        <aside className="rp-section rp-activity" aria-labelledby="rp-act-h">
          <header className="rp-sec-h">
            <div>
              <p className="rp-eyebrow">03 · Log</p>
              <h2 id="rp-act-h">Latest recorded activity</h2>
            </div>
            <div className="rp-act-meta">
              <span className="pill">{activity.recordedCount} recorded</span>
              {activityUpdatedAt && (
                <span>Updated {formatAgo(Math.max(0, Math.floor((now - activityUpdatedAt) / 1000)))}</span>
              )}
              <button
                className="rp-btn sm"
                onClick={() => loadActivity()}
                disabled={activityLoading}
                aria-label="Refresh activity"
              >
                <RefreshIcon spinning={activityLoading} />
                {activityLoading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </header>

          {activityError && (
            <p className="rp-state error">
              {activityError}
              <button className="rp-link-btn" onClick={() => loadActivity()}>
                Try again
              </button>
            </p>
          )}

          {activity.items.length === 0 ? (
            !activityError && (
              <p className="rp-act-empty">{activityLoading ? "Loading activity…" : "No recorded events yet."}</p>
            )
          ) : (
            <ul className="rp-feed">
              {activity.items.map((item) => (
                <li key={item.id} className={item.kind}>
                  <strong title={item.candidate_name}>{item.candidate_name}</strong>
                  <p title={item.text}>{item.text}</p>
                  <span className="rp-time" title={moment(item.activity_at).format("DD MMM YYYY, hh:mm A")}>
                    {moment(item.activity_at).fromNow()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
      </DrillRangeContext.Provider>
    </div>
  );
};

export default JobInsightsV2;
